import { useEffect, useMemo, useState } from 'react';
import { Chip, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import Text, { TextSpan } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { DEFAULT_TABLE_STYLES } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { DD_GRID_CELL_SLOT_PROPS } from '../../../utils/telemetry';
import { DeleteBtn } from '../../Compounds/DeleteBtn/DeleteBtn';
import { FullReportToolbar } from '../Report/ReportGridToolbar';
import { useReports } from '../../../store/Reports';
import { useScheduleSettings } from '../../../store/ScheduleSettings';
import {
  getSelectedAccountsSubject,
  isOrdersScheduledReportsEnabled,
  isRelatedInventoryReportsEnabled
} from '../../../store/User';
import { FeatureFlags, getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { ReportListItem, ReportStatus, ReportTableType } from '../../../Api/Reports/reportsApi';
import { EditReportDialog } from './EditReportDialog';
import { ReportDataViewer } from './ReportDataViewer';
import {
  formatReportDate,
  reportStatusChipColor,
  reportStatusLabel,
  tableTypeChipColor,
  tableTypeLabel
} from './reportFormatters';

type ReportTab = 'all' | 'active' | 'paused' | 'expired' | 'owned';

const SUBTEXT =
  'A running history of every scheduled report you receive or scheduled. Combined reports open with one tab per included view.';

const LoadingOverlay = () => <ComponentSpinner />;

const ACTIONS_FIELD = 'actions';

const EDITABLE_TABLE_TYPES: ReportTableType[] = ['inventory', 'project'];

// The grids offer Related Inventory and Orders reports only while their flags are on, and the API refuses writes without them.
const canEditReport = (report: ReportListItem, flags: FeatureFlags): boolean => {
  if (report.table_type === 'order') return isOrdersScheduledReportsEnabled(flags);
  if (report.table_type === 'related_project_assets') return isRelatedInventoryReportsEnabled(flags);
  return EDITABLE_TABLE_TYPES.includes(report.table_type);
};

const DELETE_COPY = {
  default: 'This deletes the report and its past runs, and its recipients stop receiving it. This cannot be undone.',
  deleting: 'Deleting the report...',
  error: 'The report could not be deleted. Please try again.'
};

export const ReportsPage = () => {
  const { reports, loading, fetchReports } = useReports();
  const { deleteSchedule } = useScheduleSettings();
  const [tab, setTab] = useState<ReportTab>('all');
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [viewerVersion, setViewerVersion] = useState(0);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(getFeatureFlagSubject().getValue());

  useEffect(() => {
    const sub = getFeatureFlagSubject().subscribe(setFeatureFlags);
    return () => sub.unsubscribe();
  }, []);

  // Fetch on mount and whenever the selected accounts change.
  useEffect(() => {
    const sub = getSelectedAccountsSubject().subscribe(() => {
      setSelectedReportId(null);
      void fetchReports(true);
    });
    return () => sub.unsubscribe();
  }, []);

  const received = useMemo(() => reports.filter((r) => r.received !== false), [reports]);
  const activeCount = useMemo(() => received.filter((r) => r.status === 'active').length, [received]);
  const pausedCount = useMemo(() => received.filter((r) => r.status === 'paused').length, [received]);
  const expiredCount = useMemo(() => received.filter((r) => r.status === 'expired').length, [received]);
  const ownedCount = useMemo(() => reports.filter((r) => r.owned).length, [reports]);

  const filtered = useMemo(() => {
    if (tab === 'owned') return reports.filter((r) => r.owned);
    return received.filter((r) => (tab === 'all' ? true : r.status === tab));
  }, [reports, received, tab]);

  const handleDelete = async (id: string) => {
    const deleted = await deleteSchedule(id);
    if (deleted) {
      setSelectedReportId((current) => (current === id ? null : current));
      await fetchReports(true);
    }
    return deleted;
  };

  const handleEdited = () => {
    if (editingReportId === selectedReportId) setViewerVersion((version) => version + 1);
    void fetchReports(true);
  };

  const actionsColumn: GridColDef<ReportListItem> = {
    field: ACTIONS_FIELD,
    headerName: '',
    width: 104,
    sortable: false,
    filterable: false,
    hideable: false,
    disableExport: true,
    align: 'center',
    renderCell: (params) => (
      <div className="flex h-full items-center">
        {canEditReport(params.row, featureFlags) && (
          <Tooltip title="Edit report">
            <IconButton
              size="small"
              aria-label={`Edit ${params.row.view_name}`}
              onClick={() => setEditingReportId(String(params.id))}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <DeleteBtn
          copyMap={DELETE_COPY}
          deleteItem={handleDelete}
          deleteItemId={String(params.id)}
          deleteTriggerDescription={`Delete ${params.row.view_name}`}
        />
      </div>
    )
  };

  const columns: GridColDef<ReportListItem>[] = [
    {
      field: 'view',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      hideable: false,
      disableExport: true,
      align: 'center',
      renderCell: (params) =>
        params.id === selectedReportId ? (
          <VisibilityIcon
            fontSize="small"
            color="primary"
          />
        ) : (
          <VisibilityOutlinedIcon
            fontSize="small"
            sx={{ color: 'action.active' }}
          />
        )
    },
    {
      field: 'view_name',
      headerName: 'View Name',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <div className="flex h-full flex-col justify-center leading-tight">
          <div>{params.value as string}</div>
          {(params.row.tab_count ?? 1) > 1 && (
            <TextSpan
              size="xs"
              color="grey6">{`${params.row.tab_count} tabs`}</TextSpan>
          )}
        </div>
      )
    },
    {
      field: 'table_type',
      headerName: 'Table',
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          label={tableTypeLabel(params.value as ReportTableType)}
          color={tableTypeChipColor(params.value as ReportTableType)}
        />
      )
    },
    {
      // The tabs alone would leave a paused report indistinguishable from a live one under All.
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          variant="outlined"
          label={reportStatusLabel(params.value as ReportStatus)}
          color={reportStatusChipColor(params.value as ReportStatus)}
        />
      )
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      flex: 1,
      minWidth: 180,
      valueFormatter: (value) => (value as string) || '—'
    },
    {
      field: 'last_run',
      headerName: 'Last Run',
      width: 150,
      valueFormatter: (value) => formatReportDate(value as string | null)
    },
    {
      field: 'next_scheduled',
      headerName: 'Next Scheduled',
      width: 160,
      valueFormatter: (value) => formatReportDate(value as string | null)
    },
    ...(tab === 'owned' ? [actionsColumn] : [])
  ];

  const itemCount = `${filtered.length} Item${filtered.length !== 1 ? 's' : ''}`;
  const showEmptyState = !loading && reports.length === 0;

  return (
    <PageLayout
      pageTitle="Reports"
      dataCy="reports-page">
      <Text
        size="sm14"
        color="grey6"
        className="mt-8 mb-4">
        {SUBTEXT}
      </Text>

      <PageCard
        title="Your Reports"
        dataCy="reports-list-card"
        headerContent={
          !showEmptyState ? (
            <Text
              size="sm14"
              color="grey6"
              className="ml-4"
              dataTestId="reports-item-count">
              {itemCount}
            </Text>
          ) : undefined
        }>
        {showEmptyState ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Icon
              type="reportsIcon"
              className="!w-12 !h-12"
            />
            <Text
              size="lg"
              weight="medium"
              color="grey8">
              No reports yet
            </Text>
            <Text
              size="sm14"
              color="grey6"
              className="text-center max-w-md">
              You&apos;re not subscribed to any scheduled reports. When a colleague schedules a report send to you, it
              will appear here.
            </Text>
          </div>
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(_, value: ReportTab) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              className="mb-2"
              sx={{ minHeight: 0, '.MuiTab-root': { textTransform: 'none', minHeight: 0 } }}>
              <Tab
                value="all"
                label={`All (${received.length})`}
                data-cy="reports-tab-all"
              />
              <Tab
                value="active"
                label={`Active (${activeCount})`}
                data-cy="reports-tab-active"
              />
              <Tab
                value="paused"
                label={`Paused (${pausedCount})`}
                data-cy="reports-tab-paused"
              />
              <Tab
                value="expired"
                label={`Expired (${expiredCount})`}
                data-cy="reports-tab-expired"
              />
              <Tab
                value="owned"
                label={`Scheduled by me (${ownedCount})`}
                data-cy="reports-tab-owned"
              />
            </Tabs>
            <div className="h-[32rem]">
              <DataGridPro
                rows={filtered}
                columns={columns}
                loading={loading}
                slots={{ loadingOverlay: LoadingOverlay, toolbar: FullReportToolbar }}
                slotProps={{ cell: DD_GRID_CELL_SLOT_PROPS, toolbar: { viewName: 'Reports' } }}
                columnHeaderHeight={DEFAULT_TABLE_STYLES.headerHeight}
                rowHeight={DEFAULT_TABLE_STYLES.rowHeight}
                sx={{ ...DEFAULT_TABLE_STYLES.sx, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                pagination
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                rowSelectionModel={selectedReportId ? [selectedReportId] : []}
                onRowClick={(params, event) => {
                  if ((event.target as HTMLElement).closest(`[data-field="${ACTIONS_FIELD}"]`)) return;
                  const id = String(params.id);
                  setSelectedReportId((prev) => (prev === id ? null : id));
                }}
                disableRowSelectionOnClick
                disableColumnMenu
              />
            </div>
          </>
        )}
      </PageCard>

      {selectedReportId && (
        <ReportDataViewer
          key={`${selectedReportId}-${viewerVersion}`}
          reportId={selectedReportId}
        />
      )}
      <EditReportDialog
        reportId={editingReportId}
        onClose={() => setEditingReportId(null)}
        onChanged={handleEdited}
        onDeleted={(id) => setSelectedReportId((current) => (current === id ? null : current))}
      />
    </PageLayout>
  );
};
